import { Button, Theme } from '@mui/material';
import styled from 'styled-components';

interface StyledButtonProps {
  sizing: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  bgcolor: string;
  theme: Theme;
}


interface OutlinedButtonProps {
  sizing: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  outlineColor: string;
  theme: Theme;
}

const calcPadding = (sizing: string, theme: any) => {
  switch (sizing) {
    case 'sm':
      return {
        padding: '7px 14px',
        fontSize: theme.typography.tsm,
      };
    case 'md':
      return {
        padding: '9px 16px',
        fontSize: theme.typography.tsm,
      };

    case 'lg':
      return {
        padding: '10px 18px',
        fontSize: theme.typography.tmd,
      };

    case 'xl':
      return {
        padding: '11px 20px',
        fontSize: theme.typography.tlg,
      };

    case '2xl':
      return {
        padding: '15px 28px',
        fontSize: theme.typography.txl,
      };
    default:
      return {
        padding: '10px',
        fontSize: theme.typography.tmd,
      };
  }
};

const getColor = (palette: string, theme: Theme) => {
  switch (palette) {
    case 'primary':
      return {
        backgroundColor: theme.palette.base.primary,
        hoverColor: theme.palette.primary[theme.palette.mode === 'dark' ? 100 : 700],
        activeColor: theme.palette.primary[200],
        border: '1px solid transparent',
      };
    case 'error':
      return {
        backgroundColor: theme.palette.base.errorPrimary,
        hoverColor: theme.palette.error[theme.palette.mode === 'dark' ? 300 : 500],
        activeColor: theme.palette.base.errorPrimary,
        border: `1px solid ${theme.palette.base.errorPrimary}`,
      };

    case 'warning':
      return {
        backgroundColor: theme.palette.warning[600],
        hoverColor: theme.palette.warning[700],
        activeColor: theme.palette.warning[600],
        border: `1px solid ${theme.palette.warning[300]}`,
      };
    case 'success':
      return {
        backgroundColor: theme.palette.success[600],
        hoverColor: theme.palette.success[700],
        activeColor: theme.palette.success[600],
        border: `1px solid ${theme.palette.success[300]}`,
      };
    case 'transparent':
      return {
        backgroundColor: 'transparent',
        hoverColor: 'transparent',
        activeColor: 'transparent',
        border: '1px solid transparent',
      };
    default:
      return {
        backgroundColor: theme.palette.primary[600],
        hoverColor: theme.palette.primary[700],
      };
  }
};

const getBorder = (palette: string, theme: Theme) => {
  switch (palette) {
    case 'primary':
      return {
        color: theme.palette.grey[900],
        hoverColor: theme.palette.primary[700],
        activeColor: theme.palette.primary[200],
        border: `1px solid ${theme.palette.grey[300]}`,
      };
    case 'error':
      return {
        color: theme.palette.mode === 'dark' ? theme.palette.error[500] : theme.palette.error[700],
        hoverColor: theme.palette.error[600],
        activeColor: theme.palette.error[600],
        border: `1px solid ${theme.palette.error[300]}`,
      };

    case 'warning':
      return {
        color: theme.palette.warning[300],
        hoverColor: theme.palette.warning[700],
        activeColor: theme.palette.warning[600],
        border: `1px solid ${theme.palette.warning[300]}`,
      };
    case 'success':
      return {
        color: theme.palette.success[300],
        hoverColor: theme.palette.success[700],
        activeColor: theme.palette.success[600],
        border: `1px solid ${theme.palette.success[300]}`,
      };
    default:
      return {
        color: theme.palette.grey[900],
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.grey[300]}`,
      };
  }
};


export const StyledButton = styled(Button)<StyledButtonProps>(({ theme, sizing, bgcolor }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  textTransform: 'capitalize',
  backgroundColor: getColor(bgcolor, theme).backgroundColor,
  padding: calcPadding(sizing, theme).padding,
  fontSize: calcPadding(sizing, theme).fontSize,
  margin: '0px',
  lineHeight: '20px',
  border: getColor(bgcolor, theme).border,
  minWidth: 'unset',
  borderRadius: '8px',
  fontWeight: '500',
  svg: {
    height: sizing === '2xl' ? '20px' : '16px',
    width: sizing === '2xl' ? '20px' : '16px',
  },
  ':hover': {
    backgroundColor: getColor(bgcolor, theme).hoverColor,
  },
  ':active': {
    backgroundColor: getColor(bgcolor, theme).activeColor,
    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #E9F2FF',
  },
}));


export const StyledOutlinedButton = styled(Button)<OutlinedButtonProps>(({ theme, sizing, outlineColor }) => ({
  color: getBorder(outlineColor, theme).color,
  textTransform: 'capitalize',
  backgroundColor: 'transparent',
  padding: calcPadding(sizing, theme).padding,
  fontSize: calcPadding(sizing, theme).fontSize,
  margin: '0px',
  lineHeight: '20px',
  border: getBorder(outlineColor, theme).border,
  minWidth: 'unset',
  borderRadius: '8px',
  fontWeight: '500',
  svg: {
    stroke: getBorder(outlineColor, theme).color,
    height: sizing === '2xl' ? '20px' : '16px',
    width: sizing === '2xl' ? '20px' : '16px',
  },
  ':hover': {
    borderColor: getBorder(outlineColor, theme).hoverColor,
  },
  ':active': {
    borderColor: getBorder(outlineColor, theme).activeColor,
    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #E9F2FF',
  },
}));
